/**
 * Rani Mode: click zoom for the product media gallery.
 *
 * Clicking a gallery image toggles data-rm-zoomed on its container; while zoomed,
 * the pointer position is stored as --rm-zoom-x / --rm-zoom-y so the CSS in
 * snippets/product-media-gallery-content.liquid scales the image around the cursor
 * and pans as the mouse moves. Leaving the image zooms out. Listeners are delegated
 * to the document so the gallery can be re-rendered without re-binding.
 */
const CONTAINER_SELECTOR =
  '.media-gallery--rm-hover-zoom .product-media-container--image:not(.dialog-zoomed-gallery *)';
const ZOOMED_ATTRIBUTE = 'data-rm-zoomed';

/** @param {number} value */
const clampPercent = (value) => Math.max(0, Math.min(100, value));

/**
 * @param {Event} event
 * @returns {HTMLElement | null}
 */
const containerFor = (event) => {
  const container = event.target instanceof Element ? event.target.closest(CONTAINER_SELECTOR) : null;
  return container instanceof HTMLElement ? container : null;
};

/**
 * @param {HTMLElement} container
 * @param {number} clientX
 * @param {number} clientY
 */
const setOrigin = (container, clientX, clientY) => {
  const rect = container.getBoundingClientRect();
  if (!rect.width || !rect.height) return;

  const x = clampPercent(((clientX - rect.left) / rect.width) * 100);
  const y = clampPercent(((clientY - rect.top) / rect.height) * 100);
  container.style.setProperty('--rm-zoom-x', `${x.toFixed(2)}%`);
  container.style.setProperty('--rm-zoom-y', `${y.toFixed(2)}%`);
};

document.addEventListener('click', (event) => {
  if (!(event instanceof MouseEvent)) return;
  const container = containerFor(event);
  if (!container) return;

  if (container.hasAttribute(ZOOMED_ATTRIBUTE)) {
    container.removeAttribute(ZOOMED_ATTRIBUTE);
    return;
  }

  setOrigin(container, event.clientX, event.clientY);
  container.setAttribute(ZOOMED_ATTRIBUTE, '');
});

document.addEventListener(
  'pointermove',
  (event) => {
    if (event.pointerType !== 'mouse') return;
    const container = containerFor(event);
    if (!container || !container.hasAttribute(ZOOMED_ATTRIBUTE)) return;
    setOrigin(container, event.clientX, event.clientY);
  },
  { passive: true }
);

// pointerout bubbles (mouseleave does not); zoom out once the pointer leaves the container.
document.addEventListener('pointerout', (event) => {
  const container = containerFor(event);
  if (!container || !container.hasAttribute(ZOOMED_ATTRIBUTE)) return;
  const next = event.relatedTarget;
  if (next instanceof Node && container.contains(next)) return;
  container.removeAttribute(ZOOMED_ATTRIBUTE);
});
